import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Stack,
    CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CancelOutlined, SaveOutlined } from "@mui/icons-material";
import { iconContainer, saveIcon, saveIconText, cancelIconText, cancelIcon } from "./Styles/MainInformationTabStyles";
import useHttp from "../../hooks/http/useHttp";
import useUI from "../../hooks/context/useUI";

interface Association {
    entityRelationshipId: number;
    name: string;
    phone: string;
    address: string;
    city: string;
    disabled: boolean;
}

interface EntityResponse {
    entityId: number;
    entityName: string;
    facilityAssociations: Association[];
    custodianAssociations: Association[];
}

export default function DisableManagement() {
    const { id } = useParams();
    const [entityData, setEntityData] = useState<EntityResponse | null>(null);
    const [originalData, setOriginalData] = useState<EntityResponse | null>(null);
    const [isChanged, setIsChanged] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { getData, putData } = useHttp();
    const { showSnackbar } = useUI();
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchData() {
            if (!id) return;
            setIsLoading(true);
            try {
                const response = await getData(`EntityRelationship/associatedentities?id=${id}`);
                const data = response?.data ?? null;
                setEntityData(data);
                setOriginalData(data);
            } catch (error) {
                console.error("Error fetching entity data:", error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [id]);

    function handleToggle(type: "facility" | "custodian", index: number) {
        setEntityData((prev) => {
            if (!prev) return prev;
            const updated = { ...prev };
            const list = type === "facility"
                ? [...updated.facilityAssociations]
                : [...updated.custodianAssociations];

            list[index] = { ...list[index], disabled: !list[index].disabled };

            if (type === "facility") updated.facilityAssociations = list;
            else updated.custodianAssociations = list;

            setIsChanged(true);
            return updated;
        });
    }

    async function handleSave() {
        if (!entityData) return;
        setIsSaving(true);
        try {
            await putData(`EntityRelationship/updatestatus?id=${id}`,entityData);
            setOriginalData(entityData);
            setIsChanged(false);
            showSnackbar(['Updated successfully!'], 'success');
        } catch (error) {
            console.error("Error saving entity data:", error);
            alert("Failed to save changes. Please try again.");
        } finally {
            setIsSaving(false);
        }
    }

    function handleCancel() {
        setEntityData(originalData);
        setIsChanged(false);
        navigate(`/entity/details/${entityData?.entityId}`);
    }

    function renderTable(data: Association[], type: "facility" | "custodian") {
        if (!data?.length) return null;
        return (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5831f" }}>
                            <TableCell sx={{ color: "white", fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: 600 }}>Phone</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: 600 }}>Address</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: 600 }}>City</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => (
                            <TableRow key={item.entityRelationshipId}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.phone}</TableCell>
                                <TableCell>{item.address}</TableCell>
                                <TableCell>{item.city}</TableCell>
                                <TableCell>
                                    <Switch
                                        checked={!item.disabled}
                                        onChange={() => handleToggle(type, index)}
                                        color="primary"
                                    />
                                    <Typography
                                        component="span"
                                        sx={{ fontSize: "0.8rem", ml: 1, fontWeight: 600 }}
                                    >
                                        {item.disabled ? "Disabled" : "Enabled"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ textAlign: "center", mt: 6 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading entity data...</Typography>
            </Box>
        );
    }

    if (!entityData) {
        return <Typography sx={{ mt: 4, textAlign: "center" }}>No Association Record Found</Typography>;
    }

    return (
        <Box sx={{ p: 4, border: "1px solid #e0e0e0", borderRadius: 2, boxShadow: 2, backgroundColor: "white" }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Association Management</Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ color: "#f57c00", fontWeight: 700 }}>
                    {entityData.entityName}
                </Typography>

                <Stack direction="row" spacing={2}>
                    <Box data-testid="cancel" sx={iconContainer} onClick={handleCancel}>
                        <CancelOutlined sx={cancelIcon} />
                        <Typography sx={cancelIconText}>Cancel</Typography>
                    </Box>

                    <Box data-testid="save" sx={iconContainer} onClick={handleSave}>
                        <SaveOutlined sx={saveIcon} />
                        <Typography sx={saveIconText}>Save</Typography>
                    </Box>
                </Stack>
            </Stack>

            {entityData.facilityAssociations?.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mt: 3, fontWeight: 600 }}>Associations towards Facilities</Typography>
                    {renderTable(entityData.facilityAssociations, "facility")}
                </>
            )}

            {entityData.custodianAssociations?.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mt: 6, fontWeight: 600 }}>Associations towards Custodians</Typography>
                    {renderTable(entityData.custodianAssociations, "custodian")}
                </>
            )}
        </Box>
    );
}
